import { createContext, useState, useContext, useCallback, useRef } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import './ConfirmDialog.css';

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((options) => {
    // Accept either a string (simple message) or an options object
    const opts = typeof options === 'string' ? { message: options } : options;
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setDialog({
        title: opts.title || 'Confirm',
        message: opts.message,
        confirmLabel: opts.confirmLabel || 'Confirm',
        cancelLabel: opts.cancelLabel || 'Cancel',
        variant: opts.variant || 'danger', // 'danger' | 'primary' | 'warning'
        entityName: opts.entityName, // e.g. offer name for context
      });
    });
  }, []);

  const handleClose = useCallback((result) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
    setDialog(null);
  }, []);

  // Handle ESC key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') handleClose(false);
  }, [handleClose]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <div className="confirm-overlay" onClick={() => handleClose(false)} onKeyDown={handleKeyDown} tabIndex={-1}>
          <div className={`confirm-dialog confirm-${dialog.variant}`} onClick={e => e.stopPropagation()}>
            <div className="confirm-header">
              <div className="confirm-icon">
                <AlertTriangle size={22} />
              </div>
              <h3>{dialog.title}</h3>
              <button className="confirm-close" onClick={() => handleClose(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="confirm-body">
              <p>{dialog.message}</p>
              {dialog.entityName && (
                <p className="confirm-entity-name">{dialog.entityName}</p>
              )}
            </div>
            <div className="confirm-actions">
              <button
                className="btn btn-secondary"
                onClick={() => handleClose(false)}
              >
                {dialog.cancelLabel}
              </button>
              <button
                className={`btn btn-${dialog.variant === 'danger' ? 'danger' : 'primary'}`}
                onClick={() => handleClose(true)}
                autoFocus
              >
                {dialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error('useConfirm must be used within ConfirmProvider');
  }
  return confirm;
}

export default ConfirmContext;
